import { Box, Stack, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { SettingsBlock, SettingsSelect } from '@repo/common/components';
import { Button, Text, Title } from '@repo/foundations';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import type { IIntegration } from '../../../../../api';
import { useUpdateIntegration } from '../../../../../api';

interface QueryPermissionsSettingsProps {
	integration: IIntegration;
}

const oauthSchema = Yup.object().shape({
	oauth_client_id: Yup.string().required('Required'),
	oauth_client_secret: Yup.string().required('Required'),
	oauth_warehouse_id: Yup.string().optional(),
});

type QueryingPermissionMode = 'service' | 'personal' | 'oauth';

const AUTH_OPTIONS = [
	{ value: 'service', label: 'Service account' },
	{ value: 'personal', label: 'Personal credentials' },
	{ value: 'oauth', label: 'OAuth' },
];

export function QueryPermissionsSettings({
	integration,
}: QueryPermissionsSettingsProps) {
	const { mutateAsync: updateIntegration } = useUpdateIntegration({});
	const [isSaving, setIsSaving] = useState(false);

	const credentials = useMemo(
		() => (integration.credentials ?? {}) as Record<string, string>,
		[integration.credentials]
	);

	const isOauthEnabled = Boolean(credentials.oauth_enabled);
	const isPersonalEnabled = Boolean(
		integration.query_execute_use_user_level_credentials
	);
	const activeMode: QueryingPermissionMode = isOauthEnabled
		? 'oauth'
		: isPersonalEnabled
			? 'personal'
			: 'service';

	const formik = useFormik({
		initialValues: {
			oauth_client_id: credentials.oauth_client_id ?? '',
			oauth_client_secret: credentials.oauth_client_secret ?? '',
			oauth_warehouse_id: credentials.oauth_warehouse_id ?? '',
		},
		validationSchema: oauthSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			setIsSaving(true);
			try {
				await updateIntegration({
					data: {
						id: integration.id,
						credentials: {
							...credentials,
							...values,
						},
					},
				});
				showNotification({
					title: 'Success',
					message: 'OAuth settings updated successfully',
					color: 'green',
				});
			} catch (error) {
				showNotification({
					title: 'Error',
					message: 'Failed to update OAuth settings',
					color: 'red',
				});
			} finally {
				setIsSaving(false);
			}
		},
	});

	const handleModeChange = async (value: string | null): Promise<void> => {
		const newMode = value as QueryingPermissionMode | null;
		if (!newMode || newMode === activeMode) {
			return;
		}

		setIsSaving(true);
		try {
			await updateIntegration({
				data: {
					id: integration.id,
					query_execute_use_user_level_credentials: newMode === 'personal',
					credentials: {
						...credentials,
						oauth_enabled: newMode === 'oauth',
					},
				},
			});
			showNotification({
				title: 'Success',
				message: 'Query authentication updated successfully',
				color: 'green',
			});
		} catch (error) {
			showNotification({
				title: 'Error',
				message: 'Failed to update query authentication',
				color: 'red',
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Stack spacing="md">
			<Stack spacing={0}>
				<Title size="md">Query authentication</Title>
				<Text size="sm" color="text/secondary/default">
					Choose how users authenticate to run queries with this integration
				</Text>
			</Stack>
			<Box>
				<SettingsSelect
					title="Authentication method"
					description="How user queries are authenticated"
					options={AUTH_OPTIONS}
					value={activeMode}
					onChange={(value) => void handleModeChange(value)}
					disabled={isSaving}
				/>
			</Box>
			{activeMode === 'oauth' && (
				<form onSubmit={formik.handleSubmit}>
					<Stack spacing={0}>
						<SettingsBlock
							title="OAuth Client ID"
							description="Client ID generated in your OAuth app"
						>
							<TextInput
								variant="filled"
								name="oauth_client_id"
								value={formik.values.oauth_client_id}
								onChange={formik.handleChange}
								error={
									formik.touched.oauth_client_id
										? formik.errors.oauth_client_id
										: undefined
								}
								disabled={isSaving}
							/>
						</SettingsBlock>
						<SettingsBlock
							title="OAuth Client Secret"
							description="Client secret generated in your OAuth app"
						>
							<TextInput
								variant="filled"
								name="oauth_client_secret"
								type="password"
								value={formik.values.oauth_client_secret}
								onChange={formik.handleChange}
								error={
									formik.touched.oauth_client_secret
										? formik.errors.oauth_client_secret
										: undefined
								}
								disabled={isSaving}
							/>
						</SettingsBlock>
						{integration.type === 'databricks' && (
							<SettingsBlock
								title="OAuth Warehouse ID"
								description="Optional SQL warehouse ID to use for queries run through OAuth. If not set, Secoda uses the first running warehouse available to the user."
							>
								<TextInput
									variant="filled"
									name="oauth_warehouse_id"
									value={formik.values.oauth_warehouse_id}
									onChange={formik.handleChange}
									error={
										formik.touched.oauth_warehouse_id
											? formik.errors.oauth_warehouse_id
											: undefined
									}
									disabled={isSaving}
								/>
							</SettingsBlock>
						)}
						<Box pt="md">
							<Button variant="primary" type="submit" loading={isSaving}>
								Save OAuth settings
							</Button>
						</Box>
					</Stack>
				</form>
			)}
		</Stack>
	);
}
